import { combineReducers } from 'redux';
import { createSelector } from 'reselect';
import { getType } from 'typesafe-actions';
import { actions } from '..';
import { toObject } from '../../data-utils';
import { ContactBasic, ContactNumberType, getKey, Identifier } from '../../model';
import { Actions, StoreState } from '../types';

interface ContactById {
  [id: string]: ContactBasic
}

function conatctById(state: ContactById | null = {}, action: Actions): ContactById | null {
  switch (action.type) {
    case getType(actions.deleteContact): {
      if (!state) { return state }
      const { contact } = action.payload
      const newState = { ...state }
      delete newState[getKey(contact)]
      return newState
    }

    case getType(actions.fetchContactsRequeust): {
      return null
    }

    case getType(actions.fetchContactsResponse): {
      return toObject(action.payload.contacts, getKey, c => c)
    }

    case getType(actions.updateContactDetails): {
      if (!state) { return state }
      const { serverName, contactId, regionId, extensionNumber, description, email } = action.payload
      const key = getKey({ server: serverName, id: contactId.toString() })
      const c = state[key]
      if (c) {
        return { ...state, [key]: { ...c, regionId, extensionNumber, description, email } }
      } else {
        return { ...state, [key]: { server: serverName, id: contactId.toString(), regionId, extensionNumber, description, email, home: '', business: '', mobile: '' } }
      }
    }

    case getType(actions.updateContactNumber): {
      if (!state) { return state }
      const { server, contactId, numberType, number } = action.payload
      const key = getKey({ server, id: contactId.toString() })
      const c = state[key]

      if (c) {
        let home = c.home
        let business = c.business
        let mobile = c.mobile

        switch (numberType) {
          case ContactNumberType.Home:
            home = number
            break;
          case ContactNumberType.Business:
            business = number
            break;
          case ContactNumberType.Mobile:
            mobile = number
            break
        }

        return { ...state, [key]: { ...c, home, business, mobile } }
      }
      break;
    }

    case getType(actions.deleteContactNumber): {
      if (!state) { return state }
      const { server, contactId, numberType } = action.payload
      const key = getKey({ server, id: contactId.toString() })
      const c = state[key]

      if (c) {
        let home = c.home
        let business = c.business
        let mobile = c.mobile

        switch (numberType) {
          case ContactNumberType.Home:
            home = ''
            break;
          case ContactNumberType.Business:
            business = ''
            break;
          case ContactNumberType.Mobile:
            mobile = ''
            break
        }

        return { ...state, [key]: { ...c, home, business, mobile } }
      }
      break;
    }
  }

  return state
}

function favourites(state: Record<string, true | undefined> = {}, action: Actions): Record<string, true | undefined> {
  switch (action.type) {
    case getType(actions.deleteContact): {
      const { contact } = action.payload
      const newState = { ...state }
      delete newState[getKey(contact)]
      return newState
    }

    case getType(actions.fetchFavContactsResponse): {
      return toObject(action.payload, getKey, c => true as const)
    }

    case getType(actions.favouriteContactAction): {
      const { add, contact } = action.payload
      const newState = { ...state }
      if (add) {
        newState[getKey(contact)] = true
      } else {
        delete newState[getKey(contact)]
      }
      return newState
    }
  }

  return state
}

export type ContactSate = 'none' | 'saving' | 'saved' | 'failed'
export type NewContactState = {
  id: Identifier,
  description?: string
}

function selectedContact(state: NewContactState | null = null, action: Actions): NewContactState | null {
  if (action.type === getType(actions.setSelectedContact)) {
    if (!action.payload.contact) {
      return null
    } else {
      return { id: action.payload.contact, description: action.payload.description }
    }
  }

  if (action.type === getType(actions.setDirectoryView)) {
    return null
  }

  if (action.type === getType(actions.deleteContact)) {
    if (state && state.id.id === action.payload.contact.id && state.id.server === action.payload.contact.server)
      return null
  }

  return state
}

function savingState(state: ContactSate = 'none', action: Actions): ContactSate {
  if (action.type === getType(actions.setSelectedContactState)) {
    if (state)
      return action.payload
  }

  return state
}

function pages(state: number = 0, action: Actions): number {
  switch (action.type) {
    case getType(actions.fetchContactsResponse): {
      return action.payload.pages
    }
  }

  return state
}

export default combineReducers({
  conatctById,
  selectedContact,
  savingState,
  pages,
  favourites,
})

function getRoot(state: StoreState) { return state.contacts }
function getContactsById(state: StoreState) { return getRoot(state).conatctById }
export function getContactSaveStatus(state: StoreState) { return getRoot(state).savingState }
export function getSelectedContact(state: StoreState) { return getRoot(state).selectedContact }
export function getContactPages(state: StoreState) { return getRoot(state).pages }
function getFavouriteContacts(state: StoreState) { return getRoot(state).favourites }

export function getContactById(state: StoreState, id: Identifier) {
  return (getContactsById(state) || {})[getKey(id)]
}

export const selectContacts = createSelector(
  getContactsById,
  (contacts): null | ContactBasic[] => {
    if (!contacts) { return null }
    return Object.values(contacts)
  },
)

export const selectContact = createSelector(
  selectContacts,
  (s: StoreState, contactId: Identifier) => contactId,
  (contacts, contactId): null | ContactBasic => {
    if (!contacts) { return null }
    const contact = contacts.find(c => c.id === contactId.id && c.server === contactId.server)
    return !contact ? null : contact
  },
)

export function isFavouriteContact(state: StoreState, contact: Identifier) {
  return getFavouriteContacts(state)[getKey(contact)] || false
}
